/**
 * A PARANCSPALETTA — `Ctrl+K` (D9 kiegészítés, 2026-08-28).
 *
 * A D9 két dolgot mond róla, és a második a fontosabb: „ez az a hely, ahova
 * a ritka műveletek mennek, és egyben a »művelet, nem oldal« szabály
 * végrehajtó eszköze". A `CLAUDE.md` §2 szerint negyedik felület nincs — a
 * szinkronizálás, az értesítés, az export MŰVELET. A paletta az a hely, ahol
 * elférnek anélkül, hogy képernyőt kapnának.
 *
 * NINCS NATÍV `<select>`, és ez a D9 kifejezett tilalma: „billentyűvel
 * csapda: megnyílik és elnyeli a `J`/`K`-t". Combobox + listbox, kézzel —
 * `aria-activedescendant`-tal, tehát a FÓKUSZ VÉGIG A MEZŐN MARAD, miközben
 * a kijelölés a listán mozog. Így lehet egyszerre gépelni és választani.
 *
 * A NYILAK MOZGATNAK, NEM A `J`/`K`. A paletta mezőjében a `j` betű BETŰ —
 * ugyanaz az érv, amiért a `useShortcuts` a beviteli mezőket kihagyja. A
 * `J`/`K` a LISTÁKON visz, a palettán a nyilak.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDialogFocus } from '@/hooks/useDialogFocus';
import { type Command, filterCommands, keepSelection, step } from './commands';

interface CommandPaletteProps {
  commands: Command[];
  onClose: () => void;
}

export function CommandPalette({ commands, onClose }: CommandPaletteProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();

  const matches = useMemo(
    () => filterCommands(commands, query, (command) => t(command.labelKey)),
    [commands, query, t]
  );

  // A KIJELÖLÉS KÖVETI A SZŰKÍTÉST. Gépelés közben a találatok fogynak; ha a
  // kijelölt parancs kiesik, az elsőre lépünk — üres kijelölés mellett az
  // `Enter` némán nem csinálna semmit.
  const selected = keepSelection(matches, selectedId);

  // A KIJELÖLT SOR MINDIG LÁTSZIK (#98). A fókusz a mezőn marad, tehát a
  // böngésző magától NEM görget a kijelöléshez — az `aria-activedescendant`
  // csak a képernyőolvasónak mondja meg, hol állunk. Enélkül a nyíl a lista
  // látható része alá vitte a kijelölést, és vakon kellett `Enter`-t nyomni.
  // A `?.` a jsdom miatt kell: ott nincs `scrollIntoView`.
  useEffect(() => {
    if (selected === null) {
      return;
    }
    document.getElementById(`${listId}-${selected}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [listId, selected]);

  // A FÓKUSZ A MEZŐRE KERÜL NYITÁSKOR. Enélkül a paletta megjelenne, és a
  // következő leütés a mögötte lévő listára menne — pont az a fajta rejtett
  // állapot, amit a 2.6/16 kizár.
  //
  // ZÁRÁSKOR PEDIG VISSZAMEGY ODA, AHONNAN JÖTT (#16/2). A paletta a munka
  // közben nyílik, egy kijelölt soron állva; a mai `Escape` után a fókusz a
  // `<body>`-n maradt, és a következő nyíl a lap tetejéről indult.
  const dialog = useDialogFocus<HTMLDivElement>(input);

  function runSelected() {
    const command = matches.find((one) => one.id === selected);
    if (!command) {
      return;
    }
    // ELŐBB ZÁRUNK, AZTÁN FUTTATUNK. Fordítva egy felületváltó parancs a
    // paletta alatt cserélné ki a képernyőt, és a bezárás már a MÁSIK
    // felületen történne meg — láthatóan megvillanva.
    onClose();
    command.run();
  }

  function onKeyDown(event: React.KeyboardEvent) {
    // A PALETTA SAJÁT BILLENTYŰI NEM SZIVÁROGNAK KI. A `useShortcuts` a
    // `window`-on ül; a `stopPropagation` nélkül az `Escape` a súgót is
    // csukná, a nyilak pedig a mögöttes listát mozgatnák.
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        setSelectedId(step(matches, selected, 1));
        return;
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        setSelectedId(step(matches, selected, -1));
        return;
      case 'Enter':
        event.preventDefault();
        event.stopPropagation();
        runSelected();
        return;
      default:
        return;
    }
  }

  return (
    <div className="overlay">
      {/* A HÁTTÉR SAJÁT GOMB, nem egy kattintható `<div>`.
          Egy `onClick`-es üres elem a képernyőolvasónak láthatatlan, a
          billentyűzetnek elérhetetlen — a D9 szerint pedig „ami csak egérrel
          működik, az nincs". Gombként viszont van neve, és az `Escape`
          mellett ez a MÁSODIK kijárat, nem az egyetlen. */}
      <button
        type="button"
        className="overlay__backdrop"
        aria-label={t('palette.close')}
        tabIndex={-1}
        onClick={onClose}
      />

      <div
        className="card palette"
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-label={t('palette.title')}
      >
        <input
          ref={input}
          className="palette__query"
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-activedescendant={selected === null ? undefined : `${listId}-${selected}`}
          aria-label={t('palette.title')}
          placeholder={t('palette.placeholder')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
        />

        {matches.length === 0 ? (
          <p className="empty">{t('palette.noMatch')}</p>
        ) : (
          /* `<div role="listbox">`, NEM `<ul>`. A lista-szemantika itt nem
             segít: a sorok szándékosan nem fókuszálhatók, a billentyűzetet a
             combobox mezője viszi (`aria-activedescendant`). Fókuszálhatóvá
             téve a `Tab` végigjárná őket, és a gépelés közbeni választás
             elveszne — épp az, amiért a paletta gyors. */
          <div className="palette__list" role="listbox" id={listId} aria-label={t('palette.title')}>
            {matches.map((command) => (
              <div
                key={command.id}
                id={`${listId}-${command.id}`}
                role="option"
                // `tabIndex={-1}`, NEM `0`. Az `aria-activedescendant`
                // mintája megköveteli, hogy a `Tab` NE járja végig a
                // sorokat — a fókusz a mezőn marad. A `-1` viszont
                // programozottan fókuszálhatóvá teszi őket, ami a szerep
                // helyes alakja, és nem elnyomott lint-szabály.
                tabIndex={-1}
                aria-selected={command.id === selected}
                className={
                  command.id === selected ? 'palette__item palette__item--on' : 'palette__item'
                }
                onMouseDown={(event) => {
                  // `onMouseDown`, NEM `onClick`: a kattintás elvenné a
                  // fókuszt a mezőtől, és a `blur` sorrendje miatt a parancs
                  // egy már bezárt paletta alatt futna le.
                  event.preventDefault();
                  onClose();
                  command.run();
                }}
              >
                <span>{t(command.labelKey)}</span>
                {command.hint ? <kbd>{command.hint}</kbd> : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
